import { toast } from 'react-toastify';
import {
    AuthenticatedAdmin,
    AuthLogin,
    IResetPasswordBody,
    LoginResponse,
    VerifiedAdminResponse,
} from '@core/models/auth.model';
import { IErrorModel, ILocalizedResponse, ISuccessResponse } from '@core/models/generic.model';
import { Action, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import AuthService from 'services/api/auth.service';
import 'react-toastify/dist/ReactToastify.css';
import { string } from 'yup';
//----------------------------------------------------------------------------------------------------------
interface ILoginState {
    admin: AuthenticatedAdmin | null;
    error: IErrorModel | undefined;
    loading: boolean;
    endSessionLoading: boolean;
    showOtpModal: boolean;
    showForgetPasswordModal: boolean;
    token: string;
}

const initialState: ILoginState = {
    admin: {} as AuthenticatedAdmin,
    error: {} as IErrorModel,
    loading: false,
    endSessionLoading: false,
    showOtpModal: false,
    showForgetPasswordModal: false,
    token: "",
};
//----------------------------------------------------------------------------------------------------------
export const loginThunk = createAsyncThunk<LoginResponse, AuthLogin, { rejectValue: IErrorModel }>(
    `/auth/login`,
    async (body, { rejectWithValue }: any) => {
        try {
            const response: any = await AuthService.login(body);
            toast.success(response.data.message, { autoClose: 5000 });
            return response.data;
        } catch (err: any) {
            toast.error(err.errorMessage, { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);
//----------------------------------------------------------------------------------------------------------
export const forgetPasswordThunk = createAsyncThunk<ISuccessResponse, string, { rejectValue: IErrorModel }>(
    `/auth/forgotPassword`,
    async (body, { rejectWithValue }: any) => {
        try {
            const response: any = await AuthService.forgetPassword(body);
            toast.success(response.data.message, { autoClose: 5000 });
            return response.data;
        } catch (err: any) {
            toast.error(
                Object.keys(err.errorMessage)
                    .map((key: string) => err.errorMessage[key])
                    .map((value: string) => value)
                    .join(` `),
                { autoClose: 10000 }
            );
            return rejectWithValue(err);
        }
    }
);
//----------------------------------------------------------------------------------------------------------
export const logoutThunk = createAsyncThunk<void, null, { rejectValue: IErrorModel }>(
    `/auth/logout`,
    async (_, { rejectWithValue }: any) => {
        try {
            const response: any = await AuthService.logout();
            return response.data.data;
        } catch (err: any) {
            const error = err;
            return rejectWithValue(error);
        }
    }
);

//----------------------------------------------------------------------------------------------------------
const loginSlice = createSlice({
    name: `login`,
    initialState,
    extraReducers: (builder) => {
        // LOGIN
        builder.addCase(loginThunk.pending, (state: any, action: any) => {
            state.loading = true;
        });
        builder.addCase(loginThunk.fulfilled, (state, action: any) => {
            state.loading = false;
            state.showOtpModal = true;
            state.token = action.payload.token;
            state.admin = action.payload.name;

        });
        builder.addCase(loginThunk.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
            state.showOtpModal = false;
        });
        // LOGOUT
        builder.addCase(logoutThunk.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(logoutThunk.fulfilled, (state) => {
            state.loading = false;
            state.admin = null;
        });
        builder.addCase(logoutThunk.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // Forget Password
        builder.addCase(forgetPasswordThunk.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(forgetPasswordThunk.fulfilled, (state) => {
            state.loading = false;
        });
        builder.addCase(forgetPasswordThunk.rejected, (state) => {
            state.loading = false;
        });
    },
    reducers: {
        toggleOtpModal(state, action) {
            state.showOtpModal = action.payload;
        },
        toggleForgetPasswordModal(state, action) {
            state.showForgetPasswordModal = action.payload;
        },
    },
});
//----------------------------------------------------------------------------------------------------------
export default loginSlice;
export const { toggleOtpModal, toggleForgetPasswordModal } = loginSlice.actions;
export const loginReducer = loginSlice.reducer;
//----------------------------------------------------------------------------------------------------------
